import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { nairaFormart } from "../../../utils/functions";
import BtnLoader from "../../loaders/btn-loader.component";
import ReportIssueConfirmationModal from "../../modals/report-issue-confirmation-modal";

const BookingCardItem = ({ property, onStatusChange }) => {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const { acf, title, slug } = property;
  
  const gallery = acf?.gallery || [];
  const coverImage = gallery.length > 0 
    ? (typeof gallery[0] === "string" ? gallery[0] : gallery[0]?.full_image_url || gallery[0]?.url) 
    : "";
  const propertyVideo = acf?.video || "";

  useEffect(() => {
    if (acf?.escrow_status !== "in_escrow" || !acf?.escrow_release_date) return;

    const calculateTimeLeft = () => {
      const releaseDate = new Date(acf.escrow_release_date.replace(" ", "T"));
      const now = new Date();
      const difference = releaseDate - now;

      if (difference <= 0) {
        setTimeLeft("Time's up");
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
      setTimeLeft(`${days}d ${hours}h remaining`);
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 60000); 
    return () => clearInterval(timer);
  }, [acf?.escrow_status, acf?.escrow_release_date]);

  const handleAction = async (newStatus, reason = null) => {
    setIsProcessing(true);
    
    try {
      // If reporting an issue, send email notification
      if (newStatus === "disputed" && reason) {
        await fetch(`${process.env.REACT_APP_BASE_URL || "https://ruumies-vfd-nodejs-engine-juft.vercel.app"}/api/emails/send-booking-report-email`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            propertyId: property.id,
            propertyTitle: title?.rendered || title,
            propertyAddress: acf?.address,
            reason: reason,
            bookingId: property.id,
            timestamp: new Date().toISOString(),
          }),
        });
      }
      
      await onStatusChange(property.id, newStatus);
    } catch (error) {
      console.error("Error during action:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNavigate = () => {
    navigate(`../properties/${slug}`);
  };
 console.log(acf)
  const renderStatusBadge = () => {
    switch (acf?.escrow_status) {
      case "in_escrow":
        return <span className="bg-primary text-white px-3 py-1.5 rounded-md text-[11px] font-bold tracking-wider uppercase shadow-sm">Funds Secured</span>;
      case "payout_ready":
        return <span className="bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-md text-[11px] font-bold tracking-wider uppercase">Move-In Confirmed</span>;
      case "disputed":
        return <span className="bg-red-50 text-red-700 border border-red-200 px-3 py-1.5 rounded-md text-[11px] font-bold tracking-wider uppercase">Under Review</span>;
      case "completed":
        return <span className="bg-neutral-100 text-neutral-600 border border-neutral-200 px-3 py-1.5 rounded-md text-[11px] font-bold tracking-wider uppercase">Completed</span>;
      case "available":
        return <span className="bg-gray-100 text-gray-600 border border-gray-200 px-3 py-1.5 rounded-md text-[11px] font-bold tracking-wider uppercase">Available</span>;
        default:
        return null;
    }
  };

  return (
    <div className="bg-white border border-neutral-100 rounded-2xl overflow-hidden shadow-sm flex flex-col md:flex-row items-stretch mb-6 hover:shadow-md transition-all duration-300">
      
      <button 
        type="button"
        className="md:w-1/3 h-full max-h-64  min-h-[130px] relative w-full text-left focus:outline-none focus:ring-4 focus:ring-primary focus:ring-opacity-50 overflow-hidden bg-neutral-100" 
        onClick={handleNavigate}
      >
        {coverImage ? (
          <img 
            src={coverImage} 
            alt={title?.rendered || title} 
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
          />
        ) : propertyVideo ? (
          <video 
            src={propertyVideo} 
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
            autoPlay 
            muted 
            loop 
            playsInline 
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-400">
            <span className="text-sm font-medium">No Media Available</span>
          </div>
        )}
        
        <div className="absolute top-4 left-4">
          {renderStatusBadge()}
        </div>
      </button>

      <div className="p-4 md:p-5 flex flex-col justify-between md:w-2/3">
        <div>
          <div className="flex justify-between items-start mb-1">
            <h3 className="text-[18px] md:text-xl font-black text-neutral-900 leading-tight pr-4">
              <button
                type="button"
                onClick={handleNavigate}
                className="cursor-pointer text-left focus:outline-none hover:text-primary transition-colors"
              >
                {title?.rendered || title}
              </button>
            </h3>
            <span className="text-lg font-black text-primary shrink-0">
              {nairaFormart(acf?.price)}
            </span>
          </div>
          <p className="text-sm text-neutral-500 font-medium mb-4 md:mb-5">{acf?.address}</p>

          {acf?.escrow_status === "in_escrow" && (
            <div className="bg-primary bg-opacity-5 rounded-xl p-4 mb-2 flex items-center justify-between">
              <span className="text-sm text-neutral-700 font-semibold">Time left to move in</span>
              <span className="text-base font-black text-primary">{timeLeft}</span>
            </div>
          )}
        </div>

        {acf?.escrow_status === "in_escrow" && (
          <div className="flex flex-col sm:flex-row gap-3 mt-5">
            <button
              onClick={() => handleAction("payout_ready")}
              disabled={isProcessing}
              className="flex-1 bg-primary hover:bg-opacity-90 text-white font-bold py-3.5 px-4 rounded-xl transition-all disabled:opacity-50 text-sm shadow-sm"
            >
              {isProcessing ? <BtnLoader /> : "Confirm Move-In"}
            </button>
            <button
              onClick={() => setShowReportModal(true)}
              disabled={isProcessing}
              className="flex-1 bg-white hover:bg-neutral-50 text-red-500 border border-neutral-200 font-bold py-3.5 px-4 rounded-xl transition-all disabled:opacity-50 text-sm"
            >
              Report an Issue
            </button>
          </div>
        )}
      </div>

      <ReportIssueConfirmationModal
        isOpen={showReportModal}
        onConfirm={(reason) => {
          setShowReportModal(false);
          handleAction("disputed", reason);
        }}
        onCancel={() => setShowReportModal(false)}
        isLoading={isProcessing}
      />
    </div>
  );
};

BookingCardItem.propTypes = {
  property: PropTypes.object.isRequired,
  onStatusChange: PropTypes.func.isRequired,
};

export default BookingCardItem;